import { useState, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Trash2, Plus } from 'lucide-react'
import {
  usePricingRates,
  useOrgCatalog,
  usePricingCategories,
  useCatalogGroups,
  usePricingMutations,
  pricingKeys,
} from '@/hooks/usePricing'
import type { ServiceType, PricingRate, ServiceCatalogItem, PricingCategory } from '@/types/pricing'
import { UNIT_OF_MEASURE_LABELS } from '@/types/pricing'
import { useQueryClient } from '@tanstack/react-query'

interface RateEditorGridProps {
  versionId: string
  serviceType: ServiceType
  readOnly?: boolean
}

export function RateEditorGrid({ versionId, serviceType, readOnly }: RateEditorGridProps) {
  const { data: allRates, isLoading: ratesLoading } = usePricingRates(versionId)
  const { data: catalog, isLoading: catalogLoading } = useOrgCatalog()
  const { data: categories } = usePricingCategories()
  const { data: groups } = useCatalogGroups()
  const mutations = usePricingMutations()
  const queryClient = useQueryClient()

  const [editingCell, setEditingCell] = useState<{
    rateId: string
    field: string
  } | null>(null)
  const [editValue, setEditValue] = useState('')

  // Filter catalog items by service type (include parking + hangar_nightly in "parking" tab)
  const filteredCatalog =
    catalog?.filter((item) => {
      if (serviceType === 'parking') {
        return item.type === 'parking' || item.type === 'hangar_nightly'
      }
      if (serviceType === 'service') {
        return item.type === 'service' || item.type === 'oil' || item.type === 'concession'
      }
      return item.type === serviceType
    }) ?? []

  // Group rates by catalog item
  const ratesByCatalogItem = new Map<string, PricingRate[]>()
  allRates?.forEach((rate) => {
    const existing = ratesByCatalogItem.get(rate.catalogItemId) ?? []
    existing.push(rate)
    ratesByCatalogItem.set(rate.catalogItemId, existing)
  })

  const invalidateRates = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: pricingKeys.rates(versionId) })
  }, [queryClient, versionId])

  const handleStartEdit = (rateId: string, field: string, currentValue: string | number | null) => {
    if (readOnly) return
    setEditingCell({ rateId, field })
    setEditValue(String(currentValue ?? ''))
  }

  const handleSaveEdit = (rate: PricingRate) => {
    if (!editingCell) return
    const { field } = editingCell

    const numericFields = ['price', 'cost_per_unit', 'volume_min_gallons', 'volume_max_gallons', 'weekly_rate', 'monthly_rate']
    const value = numericFields.includes(field)
      ? editValue ? Number(editValue) : null
      : editValue || null

    mutations.saveRate.mutate(
      {
        id: rate.id,
        version_id: rate.versionId,
        catalog_item_id: rate.catalogItemId,
        pricing_category_id: rate.pricingCategoryId,
        [field]: value,
      },
      { onSuccess: invalidateRates }
    )
    setEditingCell(null)
  }

  const handleKeyDown = (e: React.KeyboardEvent, rate: PricingRate) => {
    if (e.key === 'Enter') {
      handleSaveEdit(rate)
    } else if (e.key === 'Escape') {
      setEditingCell(null)
    }
  }

  const handleAddRate = (catalogItem: ServiceCatalogItem, category?: PricingCategory) => {
    mutations.saveRate.mutate(
      {
        version_id: versionId,
        catalog_item_id: catalogItem.id,
        pricing_category_id: category?.id ?? null,
        price: 0,
        sort_order: (ratesByCatalogItem.get(catalogItem.id)?.length ?? 0) + 1,
      },
      { onSuccess: invalidateRates }
    )
  }

  const handleRemoveRate = (rateId: string) => {
    mutations.removeRate.mutate(rateId, { onSuccess: invalidateRates })
  }

  if (ratesLoading || catalogLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  // Group catalog items by their group for "services" tab
  const groupMap = new Map(groups?.map((g) => [g.id, g]) ?? [])
  const isServicesTab = serviceType === 'service'

  const groupedCatalog = isServicesTab
    ? (() => {
        const byGroup = new Map<string, ServiceCatalogItem[]>()
        for (const item of filteredCatalog) {
          const key = item.groupId ?? '__ungrouped__'
          const list = byGroup.get(key) ?? []
          list.push(item)
          byGroup.set(key, list)
        }
        // Sort groups by group sort_order
        return Array.from(byGroup.entries()).sort(([a], [b]) => {
          const ga = a === '__ungrouped__' ? 999 : (groupMap.get(a)?.sortOrder ?? 999)
          const gb = b === '__ungrouped__' ? 999 : (groupMap.get(b)?.sortOrder ?? 999)
          return ga - gb
        })
      })()
    : null

  if (filteredCatalog.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No catalog items for this service type.</p>
        <p className="text-sm mt-1">Add items in the Service Catalog tab first.</p>
      </div>
    )
  }

  const isFuelType = serviceType === 'fuel'

  const renderCatalogItem = (catalogItem: ServiceCatalogItem) => {
    const rates = ratesByCatalogItem.get(catalogItem.id) ?? []
    return (
      <div key={catalogItem.id} className="border rounded-lg">
        <div className="flex items-center justify-between px-4 py-3 bg-muted/50 rounded-t-lg">
          <div>
            <span className="font-medium">{catalogItem.name}</span>
            <span className="text-sm text-muted-foreground ml-2">
              ({UNIT_OF_MEASURE_LABELS[catalogItem.unitOfMeasure]})
            </span>
            {catalogItem.fuelType && (
              <span className="text-xs text-muted-foreground ml-2 uppercase">
                {catalogItem.fuelType.replace('_', ' ')}
              </span>
            )}
          </div>
          {!readOnly && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleAddRate(catalogItem)}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add Rate
            </Button>
          )}
        </div>

            {rates.length === 0 ? (
              <div className="px-4 py-4 text-sm text-muted-foreground text-center">
                No rates configured.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead className="w-[120px]">Price</TableHead>
                    <TableHead className="w-[120px]">Cost/Unit</TableHead>
                    {isFuelType && (
                      <>
                        <TableHead className="w-[100px]">Vol Min</TableHead>
                        <TableHead className="w-[100px]">Vol Max</TableHead>
                      </>
                    )}
                    <TableHead>Notes</TableHead>
                    {!readOnly && <TableHead className="w-[50px]" />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rates.map((rate) => {
                    const category = categories?.find((c) => c.id === rate.pricingCategoryId)
                    return (
                      <TableRow key={rate.id}>
                        <TableCell className="text-sm">
                          {category?.name ?? 'Default'}
                        </TableCell>
                        <EditableCell
                          value={rate.price}
                          rateId={rate.id}
                          field="price"
                          editingCell={editingCell}
                          editValue={editValue}
                          onStartEdit={handleStartEdit}
                          onSaveEdit={() => handleSaveEdit(rate)}
                          onKeyDown={(e) => handleKeyDown(e, rate)}
                          onEditValueChange={setEditValue}
                          format="currency"
                        />
                        <EditableCell
                          value={rate.costPerUnit}
                          rateId={rate.id}
                          field="cost_per_unit"
                          editingCell={editingCell}
                          editValue={editValue}
                          onStartEdit={handleStartEdit}
                          onSaveEdit={() => handleSaveEdit(rate)}
                          onKeyDown={(e) => handleKeyDown(e, rate)}
                          onEditValueChange={setEditValue}
                          format="currency"
                        />
                        {isFuelType && (
                          <>
                            <EditableCell
                              value={rate.volumeMinGallons}
                              rateId={rate.id}
                              field="volume_min_gallons"
                              editingCell={editingCell}
                              editValue={editValue}
                              onStartEdit={handleStartEdit}
                              onSaveEdit={() => handleSaveEdit(rate)}
                              onKeyDown={(e) => handleKeyDown(e, rate)}
                              onEditValueChange={setEditValue}
                              format="number"
                            />
                            <EditableCell
                              value={rate.volumeMaxGallons}
                              rateId={rate.id}
                              field="volume_max_gallons"
                              editingCell={editingCell}
                              editValue={editValue}
                              onStartEdit={handleStartEdit}
                              onSaveEdit={() => handleSaveEdit(rate)}
                              onKeyDown={(e) => handleKeyDown(e, rate)}
                              onEditValueChange={setEditValue}
                              format="number"
                            />
                          </>
                        )}
                        <EditableCell
                          value={rate.notes}
                          rateId={rate.id}
                          field="notes"
                          editingCell={editingCell}
                          editValue={editValue}
                          onStartEdit={handleStartEdit}
                          onSaveEdit={() => handleSaveEdit(rate)}
                          onKeyDown={(e) => handleKeyDown(e, rate)}
                          onEditValueChange={setEditValue}
                          format="text"
                        />
                        {!readOnly && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive"
                              onClick={() => handleRemoveRate(rate.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
      </div>
    )
  }

  if (groupedCatalog) {
    return (
      <div className="space-y-6">
        {groupedCatalog.map(([groupId, items]) => {
          const group = groupId !== '__ungrouped__' ? groupMap.get(groupId) : null
          return (
            <div key={groupId}>
              <h4 className="text-sm font-medium text-muted-foreground mb-3 border-b pb-1">
                {group?.name ?? 'Other'}
              </h4>
              <div className="space-y-4">
                {items.map(renderCatalogItem)}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {filteredCatalog.map(renderCatalogItem)}
    </div>
  )
}

function EditableCell({
  value,
  rateId,
  field,
  editingCell,
  editValue,
  onStartEdit,
  onSaveEdit,
  onKeyDown,
  onEditValueChange,
  format,
}: {
  value: string | number | null
  rateId: string
  field: string
  editingCell: { rateId: string; field: string } | null
  editValue: string
  onStartEdit: (rateId: string, field: string, value: string | number | null) => void
  onSaveEdit: () => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onEditValueChange: (v: string) => void
  format: 'currency' | 'number' | 'text'
}) {
  const isEditing = editingCell?.rateId === rateId && editingCell?.field === field

  if (isEditing) {
    return (
      <TableCell>
        <Input
          autoFocus
          value={editValue}
          onChange={(e) => onEditValueChange(e.target.value)}
          onBlur={onSaveEdit}
          onKeyDown={onKeyDown}
          className="h-7 text-sm"
          type={format === 'text' ? 'text' : 'number'}
          step={format === 'currency' ? '0.01' : '1'}
        />
      </TableCell>
    )
  }

  let display: string
  if (value == null || value === '') {
    display = '-'
  } else if (format === 'currency') {
    display = `$${Number(value).toFixed(2)}`
  } else {
    display = String(value)
  }

  return (
    <TableCell
      className="cursor-pointer hover:bg-muted/50 text-sm"
      onClick={() => onStartEdit(rateId, field, value)}
    >
      {display}
    </TableCell>
  )
}
